// src/feedback/feedbackAgentClient.ts — Client Service & Offline Heuristic Dialogue Engine

import type {
  ChatMessage,
  FeedbackAgentRequest,
  FeedbackAgentResponse,
  FeedbackCategory,
  FeedbackSeverity,
  TelemetrySnapshot,
} from './feedbackTypes';
import { ProfileManager } from '../storage/profileManager';
import { StreakManager } from '../storage/streakManager';
import { ExplorerPointsManager } from '../storage/explorerPoints';
import { SessionGuard } from '../storage/sessionGuard';

const CLIENT_ERROR_LOG: string[] = [];
const MAX_ERROR_LOGS = 5;

// Global error listener to automatically capture unhandled client errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const errText = `${event.message} at ${event.filename}:${event.lineno}`;
    recordClientError(errText);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const errText = `Unhandled rejection: ${event.reason}`;
    recordClientError(errText);
  });
}

export function recordClientError(err: string): void {
  CLIENT_ERROR_LOG.push(err);
  if (CLIENT_ERROR_LOG.length > MAX_ERROR_LOGS) {
    CLIENT_ERROR_LOG.shift();
  }
}

export function getClientErrors(): string[] {
  return [...CLIENT_ERROR_LOG];
}

export class FeedbackAgentClient {
  /**
   * Captures an instant telemetry snapshot of current application state.
   */
  static captureTelemetry(): TelemetrySnapshot {
    let streakDays = 0;
    let isFrozen = false;
    let totalEp = 0;
    let sessionMinutesToday = 0;
    let activeProfile: 'tudor' | 'tester' = 'tudor';

    try {
      const status = StreakManager.checkStatus();
      streakDays = status.currentStreak;
      isFrozen = status.isFrozen;
    } catch {
      // fallback for detached environments
    }

    try {
      totalEp = ExplorerPointsManager.getTotalEp();
    } catch {
      // fallback
    }

    try {
      sessionMinutesToday = SessionGuard.getTodayPracticeMinutes();
    } catch {
      // fallback
    }

    try {
      activeProfile = ProfileManager.getActiveProfile();
    } catch {
      // fallback
    }

    const width = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const height = typeof window !== 'undefined' ? window.innerHeight : 800;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const route = typeof window !== 'undefined' ? window.location.hash || '#/' : '#/';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';

    return {
      route,
      activeProfile,
      timestamp: new Date().toISOString(),
      viewport: { width, height, dpr },
      stats: {
        streakDays,
        isFrozen,
        totalEp,
        sessionMinutesToday,
      },
      recentErrors: getClientErrors(),
      userAgent,
    };
  }

  /**
   * Dispatches conversation to Gemini API with automatic fallback to heuristic interviewer.
   */
  static async requestFeedbackResponse(
    messages: ChatMessage[],
    telemetry: TelemetrySnapshot
  ): Promise<FeedbackAgentResponse> {
    const payload: FeedbackAgentRequest = {
      messages: messages.map((m) => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        text: m.text,
        image: m.image,
      })),
      telemetry,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);

      const res = await fetch('/api/feedback/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (res.ok) {
        const data: FeedbackAgentResponse = await res.json();
        return {
          ...data,
          source: 'gemini',
        };
      }
    } catch {
      // Fallback seamlessly to offline heuristic
    }

    return this.getHeuristicFallbackResponse(messages, telemetry);
  }

  /**
   * Intelligent Victorian Naturalist Heuristic Interviewer
   * Runs client-side when offline, during network lag, or without an active server proxy.
   */
  static getHeuristicFallbackResponse(
    messages: ChatMessage[],
    telemetry: TelemetrySnapshot
  ): FeedbackAgentResponse {
    const userTurns = messages.filter((m) => m.role === 'user');
    const latestUserMsg = userTurns[userTurns.length - 1];
    const latestUserTurn = latestUserMsg?.text.toLowerCase() || '';
    const hasImage = Boolean(latestUserMsg?.image);
    const turnCount = userTurns.length;

    // Station-aware route context
    const routeName = this.formatRouteName(telemetry.route);

    // Turn 1: Initial user observation
    if (turnCount <= 1) {
      if (hasImage && (!latestUserTurn || latestUserTurn.includes('attached') || latestUserTurn.includes('specimen') || latestUserTurn.includes('screenshot'))) {
        return {
          reply: `A splendid visual specimen! I have logged this specimen into our field logbook. Does this image capture a visual overlap, an unresponsive button, or an ADHD friction point in ${routeName}?`,
          quickChips: ['Display / visual overlap', 'Buttons didn\'t respond', 'ADHD pacing note', 'Looks fine now'],
          extractedCategory: 'bug',
          extractedSeverity: 'medium',
          suggestedTitle: `Visual specimen recorded in ${routeName}`,
          suggestedAction: `Inspect visual telemetry and attached screenshot in ${routeName}`,
          isResolvedOrFinalized: false,
          source: 'heuristic',
        };
      }
      if (
        latestUserTurn.includes('bug') ||
        latestUserTurn.includes('glitch') ||
        latestUserTurn.includes('broken') ||
        latestUserTurn.includes('error') ||
        latestUserTurn.includes('stuck')
      ) {
        return {
          reply: `A keen catch! Did this anomaly occur during tactile interaction in ${routeName}, or did an element fail to render on screen?`,
          quickChips: ['Tactile touch / drag issue', 'Display / visual overlap', 'Audio did not play', 'Frozen screen'],
          extractedCategory: 'bug',
          extractedSeverity: 'high',
          suggestedTitle: `Issue observed in ${routeName}`,
          suggestedAction: `Inspect event listeners and DOM rendering in ${routeName}`,
          isResolvedOrFinalized: false,
          source: 'heuristic',
        };
      }

      if (
        latestUserTurn.includes('adhd') ||
        latestUserTurn.includes('overwhelm') ||
        latestUserTurn.includes('stress') ||
        latestUserTurn.includes('anxiety') ||
        latestUserTurn.includes('fast') ||
        latestUserTurn.includes('timer') ||
        latestUserTurn.includes('distract')
      ) {
        return {
          reply: `A most essential observation for Tudor's cognitive wellbeing. Did the pacing feel pressing, or would calming Zen Mode and distraction-free focus help here?`,
          quickChips: ['Timer caused anxiety', 'Visuals too busy', 'Focus mode was helpful', 'Needs slower pace'],
          extractedCategory: 'adhd_friction',
          extractedSeverity: 'medium',
          suggestedTitle: `Cognitive pacing / ADHD ergonomics in ${routeName}`,
          suggestedAction: `Calibrate Zen Mode transitions and single-question focus in ${routeName}`,
          isResolvedOrFinalized: false,
          source: 'heuristic',
        };
      }

      if (
        latestUserTurn.includes('question') ||
        latestUserTurn.includes('math') ||
        latestUserTurn.includes('english') ||
        latestUserTurn.includes('11+') ||
        latestUserTurn.includes('passage') ||
        latestUserTurn.includes('clue') ||
        latestUserTurn.includes('answer')
      ) {
        return {
          reply: `Ah, scholarly precision! Was the wording of the question ambiguous, or did the Socratic hint give away too little or too much?`,
          quickChips: ['Wording ambiguous', 'Hint was too vague', 'Passage proof missing', 'Difficulty spot-on'],
          extractedCategory: 'curriculum',
          extractedSeverity: 'medium',
          suggestedTitle: `Curriculum calibrated check in ${routeName}`,
          suggestedAction: `Verify question bank rationale and proof highlight mapping`,
          isResolvedOrFinalized: false,
          source: 'heuristic',
        };
      }

      if (
        latestUserTurn.includes('love') ||
        latestUserTurn.includes('great') ||
        latestUserTurn.includes('amazing') ||
        latestUserTurn.includes('praise') ||
        latestUserTurn.includes('nice') ||
        latestUserTurn.includes('good')
      ) {
        return {
          reply: `Splendid! Clawey brandishes his claws in crustacean celebration! What delighted you most about this part of the Atelier?`,
          quickChips: ['The Victorian aesthetics', 'Socratic mascot nudges', 'Audio & tactile feedback', 'Zero-punishment streaks'],
          extractedCategory: 'praise',
          extractedSeverity: 'low',
          suggestedTitle: `Commendation for ${routeName}`,
          suggestedAction: `Document as successful pedagogical pattern`,
          isResolvedOrFinalized: false,
          source: 'heuristic',
        };
      }

      if (
        latestUserTurn.includes('feature') ||
        latestUserTurn.includes('add') ||
        latestUserTurn.includes('could') ||
        latestUserTurn.includes('idea') ||
        latestUserTurn.includes('suggest') ||
        latestUserTurn.includes('wish')
      ) {
        return {
          reply: `An inventive expedition concept! How do you envision this supporting Tudor's Bexley 11+ journey?`,
          quickChips: ['Builds confidence', 'Helps memory retention', 'More fun / playful', 'Saves practice time'],
          extractedCategory: 'feature',
          extractedSeverity: 'medium',
          suggestedTitle: `Feature suggestion for ${routeName}`,
          suggestedAction: `Review for future sprint backlog inclusion`,
          isResolvedOrFinalized: false,
          source: 'heuristic',
        };
      }

      // Default opening
      return {
        reply: `Greetings from the Archivist's desk! How did your expedition in ${routeName} feel, and what should we refine?`,
        quickChips: ['Spotted a glitch', 'Pacing or ADHD note', 'Question wording check', 'Delightful experience'],
        extractedCategory: 'adhd_friction',
        extractedSeverity: 'medium',
        suggestedTitle: `Observation in ${routeName}`,
        suggestedAction: `Review user feedback in ${routeName}`,
        isResolvedOrFinalized: false,
        source: 'heuristic',
      };
    }

    // Turn 2+: Convergence & Finalization
    const category = this.inferCategory(messages);
    const severity: FeedbackSeverity = latestUserTurn.includes('block') || latestUserTurn.includes('crash') ? 'blocker' : 'medium';

    return {
      reply: `Your diagnosis is logged into the Atelier Field Logbook with full telemetry. Would you like to record anything further, or shall we seal this specimen report?`,
      quickChips: ['Seal and Save Report', 'Add one more detail', 'Copy as GitHub Issue'],
      extractedCategory: category,
      extractedSeverity: severity,
      suggestedTitle: `${this.formatCategoryTitle(category)} in ${routeName}`,
      suggestedAction: `Address feedback in ${routeName} during next sprint`,
      isResolvedOrFinalized: true,
      source: 'heuristic',
    };
  }

  private static formatRouteName(hash: string): string {
    const routeMap: Record<string, string> = {
      '#/': 'Expedition Map',
      '#/read': 'Comprehension (Clue Hunter)',
      '#/context-lens': 'Context Lens (Vocabulary)',
      '#/fact-diver': 'Fact Diver (Rapid Retrieval)',
      '#/spelling': 'Spelling Reef',
      '#/sprint': 'Quest Sprint Station',
      '#/mock': 'Full Bexley Mock Exam',
      '#/geometry': 'Tactile Geometry Sandbox',
      '#/scan': 'Schoolwork Scanner',
      '#/dashboard': 'Parent Analytics Deck',
      '#/daily-review': 'Daily Spaced Review',
      '#/gallery': 'Naturalist Studio & Gallery',
    };
    return routeMap[hash] || 'the Atelier';
  }

  private static inferCategory(messages: ChatMessage[]): FeedbackCategory {
    const combined = messages.map((m) => m.text.toLowerCase()).join(' ');
    if (combined.includes('bug') || combined.includes('glitch') || combined.includes('broken') || combined.includes('touch')) {
      return 'bug';
    }
    if (combined.includes('adhd') || combined.includes('stress') || combined.includes('timer') || combined.includes('overwhelm')) {
      return 'adhd_friction';
    }
    if (combined.includes('question') || combined.includes('proof') || combined.includes('curriculum') || combined.includes('word')) {
      return 'curriculum';
    }
    if (combined.includes('love') || combined.includes('great') || combined.includes('praise')) {
      return 'praise';
    }
    if (combined.includes('feature') || combined.includes('suggest') || combined.includes('idea')) {
      return 'feature';
    }
    return 'adhd_friction';
  }

  private static formatCategoryTitle(category: FeedbackCategory): string {
    switch (category) {
      case 'bug':
        return 'Bug report';
      case 'adhd_friction':
        return 'ADHD ergonomics observation';
      case 'curriculum':
        return '11+ Curriculum calibration';
      case 'feature':
        return 'Feature enhancement proposal';
      case 'praise':
        return 'Pedagogical commendation';
    }
  }
}
